import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { App } from '@/App'
import { SubdomainStorefront } from '@/components/storefront/SubdomainStorefront'
import { ProtectedRoute, AdminRoute } from './ProtectedRoute'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'

// Critical pages loaded eagerly (initial render path)
import { HomePage } from '@/pages/Home/HomePage'
import { BrowsePage } from '@/pages/Browse/BrowsePage'
import { ListingPage } from '@/pages/Listing/ListingPage'
import { NotFoundPage } from '@/pages/NotFound/NotFoundPage'

/**
 * Wrapper around lazy() that auto-reloads on chunk load failure.
 * After a deploy, cached HTML may reference old chunk filenames.
 * This catches the error and does a single hard reload to fetch fresh HTML.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry(importFn: () => Promise<any>) {
  return lazy(() =>
    importFn().catch(() => {
      const hasReloaded = sessionStorage.getItem('chunk-reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
        // Return a never-resolving promise while the page reloads
        return new Promise(() => {});
      }
      sessionStorage.removeItem('chunk-reload');
      return importFn();
    })
  );
}

// Lazy-loaded pages (code-split for smaller initial bundle)
const ProductPage = lazyWithRetry(() => import('@/pages/Product/ProductPage').then(m => ({ default: m.ProductPage })))
const MarineAboutPage = lazyWithRetry(() => import('@/pages/Store/AboutPage').then(m => ({ default: m.AboutPage })))
const MarineContactPage = lazyWithRetry(() => import('@/pages/Store/ContactPage').then(m => ({ default: m.ContactPage })))
const MarineFaqPage = lazyWithRetry(() => import('@/pages/Store/FaqPage').then(m => ({ default: m.FaqPage })))
const CategoriesPage = lazyWithRetry(() => import('@/pages/Categories/CategoriesPage').then(m => ({ default: m.CategoriesPage })))
const CreateListingPage = lazyWithRetry(() => import('@/pages/Create/CreateListingPage').then(m => ({ default: m.CreateListingPage })))
const ProfilePage = lazyWithRetry(() => import('@/pages/Profile/ProfilePage').then(m => ({ default: m.ProfilePage })))
const EditProfilePage = lazyWithRetry(() => import('@/pages/Profile/EditProfilePage').then(m => ({ default: m.EditProfilePage })))
const AccountSettingsPage = lazyWithRetry(() => import('@/pages/Profile/AccountSettingsPage').then(m => ({ default: m.AccountSettingsPage })))
const BugReportPage = lazyWithRetry(() => import('@/pages/BugReport/BugReportPage').then(m => ({ default: m.BugReportPage })))
const StorefrontPage = lazyWithRetry(() => import('@/pages/Storefront/StorefrontPage').then(m => ({ default: m.StorefrontPage })))
const MyListingsPage = lazyWithRetry(() => import('@/pages/Profile/MyListingsPage').then(m => ({ default: m.MyListingsPage })))
const WatchlistPage = lazyWithRetry(() => import('@/pages/Profile/WatchlistPage').then(m => ({ default: m.WatchlistPage })))
const ReviewsPage = lazyWithRetry(() => import('@/pages/Profile/ReviewsPage').then(m => ({ default: m.ReviewsPage })))
const MilestonesPage = lazyWithRetry(() => import('@/pages/Profile/MilestonesPage').then(m => ({ default: m.MilestonesPage })))
const OffersPage = lazyWithRetry(() => import('@/pages/Profile/OffersPage').then(m => ({ default: m.OffersPage })))
const MessagesLayout = lazyWithRetry(() => import('@/pages/Messages/MessagesLayout').then(m => ({ default: m.MessagesLayout })))
const CheckoutPage = lazyWithRetry(() => import('@/pages/Checkout/CheckoutPage').then(m => ({ default: m.CheckoutPage })))
const OrdersPage = lazyWithRetry(() => import('@/pages/Orders/OrdersPage').then(m => ({ default: m.OrdersPage })))
const SellerOrdersPage = lazyWithRetry(() => import('@/pages/Orders/SellerOrdersPage').then(m => ({ default: m.SellerOrdersPage })))
const OrderDetailPage = lazyWithRetry(() => import('@/pages/Orders/OrderDetailPage').then(m => ({ default: m.OrderDetailPage })))
const FundraiserReportPage = lazyWithRetry(() => import('@/pages/Fundraiser/FundraiserReportPage').then(m => ({ default: m.FundraiserReportPage })))
const NotificationsPage = lazyWithRetry(() => import('@/pages/Notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })))
const AboutPage = lazyWithRetry(() => import('@/pages/Static/AboutPage').then(m => ({ default: m.AboutPage })))
const HowItWorksPage = lazyWithRetry(() => import('@/pages/Static/HowItWorksPage').then(m => ({ default: m.HowItWorksPage })))
const FAQPage = lazyWithRetry(() => import('@/pages/Static/FAQPage').then(m => ({ default: m.FAQPage })))
const ContactPage = lazyWithRetry(() => import('@/pages/Static/ContactPage').then(m => ({ default: m.ContactPage })))
const PricingPage = lazyWithRetry(() => import('@/pages/Static/PricingPage').then(m => ({ default: m.PricingPage })))
const PrivacyPage = lazyWithRetry(() => import('@/pages/Static/PrivacyPage').then(m => ({ default: m.PrivacyPage })))
const TermsPage = lazyWithRetry(() => import('@/pages/Static/TermsPage').then(m => ({ default: m.TermsPage })))
const AdminPage = lazyWithRetry(() => import('@/pages/Admin/AdminPage').then(m => ({ default: m.AdminPage })))
const SignupPage = lazyWithRetry(() => import('@/pages/Auth/SignupPage').then(m => ({ default: m.SignupPage })))
const CustomSubscribePage = lazyWithRetry(() => import('@/pages/Subscribe/CustomSubscribePage').then(m => ({ default: m.CustomSubscribePage })))
const AuthActionPage = lazyWithRetry(() => import('@/pages/Auth/AuthActionPage').then(m => ({ default: m.AuthActionPage })))
const VerifyEmailPage = lazyWithRetry(() => import('@/pages/Auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })))
const AccountOverviewPage = lazyWithRetry(() => import('@/pages/Account/AccountOverviewPage').then(m => ({ default: m.AccountOverviewPage })))
const BillingPage = lazyWithRetry(() => import('@/pages/Account/BillingPage').then(m => ({ default: m.BillingPage })))
const AccountShell = lazyWithRetry(() => import('@/components/layout/AccountShell').then(m => ({ default: m.AccountShell })))
const AdminShell = lazyWithRetry(() => import('@/components/layout/AdminShell').then(m => ({ default: m.AdminShell })))

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <LoadingSpinner size="lg" />
    </div>
  )
}

function SuspenseWrap({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

/**
 * Create a router for subdomain access (e.g., storename.isqroll.co.nz).
 * The index route renders the StorefrontPage for the detected slug.
 * All other routes (listing pages, auth, etc.) still work normally.
 */
export function createSubdomainRouter(slug: string, sellerId?: string) {
  return createBrowserRouter([
    {
      path: '/',
      element: <App />,
      children: [
        // Subdomain index → storefront for this slug
        { index: true, element: <SuspenseWrap><SubdomainStorefront slug={slug} sellerId={sellerId} /></SuspenseWrap> },
        // Single-store build: browse/categories would show the whole iSQROLL
        // marketplace (all sellers), so redirect them back into the store.
        { path: 'browse', element: <Navigate to="/" replace /> },
        { path: 'categories', element: <Navigate to="/" replace /> },
        { path: 'listing/:id', element: <SuspenseWrap><ProductPage /></SuspenseWrap> },
        { path: 'seller/:sellerId', element: <SuspenseWrap><StorefrontPage /></SuspenseWrap> },
        { path: 'store/:slug', element: <SuspenseWrap><StorefrontPage /></SuspenseWrap> },
        { path: 'about', element: <SuspenseWrap><MarineAboutPage /></SuspenseWrap> },
        { path: 'faq', element: <SuspenseWrap><MarineFaqPage /></SuspenseWrap> },
        { path: 'contact', element: <SuspenseWrap><MarineContactPage /></SuspenseWrap> },
        { path: 'pricing', element: <SuspenseWrap><PricingPage /></SuspenseWrap> },
        { path: 'privacy', element: <SuspenseWrap><PrivacyPage /></SuspenseWrap> },
        { path: 'terms', element: <SuspenseWrap><TermsPage /></SuspenseWrap> },
        { path: 'signup', element: <SuspenseWrap><SignupPage /></SuspenseWrap> },
        { path: 'subscribe/:offerId', element: <SuspenseWrap><CustomSubscribePage /></SuspenseWrap> },
        {
          element: <ProtectedRoute />,
          children: [
            { path: 'create', element: <SuspenseWrap><CreateListingPage /></SuspenseWrap> },
            { path: 'listing/:id/edit', element: <SuspenseWrap><CreateListingPage /></SuspenseWrap> },
            { path: 'profile', element: <SuspenseWrap><ProfilePage /></SuspenseWrap> },
            { path: 'profile/edit', element: <SuspenseWrap><EditProfilePage /></SuspenseWrap> },
            { path: 'profile/settings', element: <SuspenseWrap><AccountSettingsPage /></SuspenseWrap> },
            { path: 'messages', element: <SuspenseWrap><MessagesLayout /></SuspenseWrap> },
            { path: 'messages/:conversationId', element: <SuspenseWrap><MessagesLayout /></SuspenseWrap> },
            { path: 'checkout', element: <SuspenseWrap><CheckoutPage /></SuspenseWrap> },
            { path: 'orders', element: <SuspenseWrap><OrdersPage /></SuspenseWrap> },
            { path: 'orders/:id', element: <SuspenseWrap><OrderDetailPage /></SuspenseWrap> },
            { path: 'cart', element: <SuspenseWrap><CheckoutPage /></SuspenseWrap> },
            { path: 'fundraiser/:id/report', element: <SuspenseWrap><FundraiserReportPage /></SuspenseWrap> },
          ],
        },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ])
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      // Public routes (eagerly loaded — critical path)
      { index: true, element: <HomePage /> },
      { path: 'browse', element: <BrowsePage /> },
      { path: 'categories', element: <SuspenseWrap><CategoriesPage /></SuspenseWrap> },
      { path: 'listing/:id', element: <ListingPage /> },
      { path: 'seller/:sellerId', element: <SuspenseWrap><StorefrontPage /></SuspenseWrap> },
      { path: 'store/:slug', element: <SuspenseWrap><StorefrontPage /></SuspenseWrap> },

      // Static pages (lazy-loaded)
      { path: 'about', element: <SuspenseWrap><AboutPage /></SuspenseWrap> },
      { path: 'how-it-works', element: <SuspenseWrap><HowItWorksPage /></SuspenseWrap> },
      { path: 'faq', element: <SuspenseWrap><FAQPage /></SuspenseWrap> },
      { path: 'contact', element: <SuspenseWrap><ContactPage /></SuspenseWrap> },
      { path: 'pricing', element: <SuspenseWrap><PricingPage /></SuspenseWrap> },
      { path: 'privacy', element: <SuspenseWrap><PrivacyPage /></SuspenseWrap> },
      { path: 'terms', element: <SuspenseWrap><TermsPage /></SuspenseWrap> },
      { path: 'bug-report', element: <SuspenseWrap><BugReportPage /></SuspenseWrap> },
      { path: 'signup', element: <SuspenseWrap><SignupPage /></SuspenseWrap> },
      { path: 'auth/action', element: <SuspenseWrap><AuthActionPage /></SuspenseWrap> },
      { path: 'verify-email', element: <SuspenseWrap><VerifyEmailPage /></SuspenseWrap> },

      // Protected routes (lazy-loaded, require authentication)
      {
        element: <ProtectedRoute />,
        children: [
          // Non-account-shell routes (full-width or special layouts)
          { path: 'create', element: <SuspenseWrap><CreateListingPage /></SuspenseWrap> },
          { path: 'listing/:id/edit', element: <SuspenseWrap><CreateListingPage /></SuspenseWrap> },
          { path: 'profile', element: <SuspenseWrap><ProfilePage /></SuspenseWrap> },
          { path: 'checkout', element: <SuspenseWrap><CheckoutPage /></SuspenseWrap> },
          { path: 'cart', element: <SuspenseWrap><CheckoutPage /></SuspenseWrap> },
          { path: 'fundraiser/:id/report', element: <SuspenseWrap><FundraiserReportPage /></SuspenseWrap> },

          // Account-shell routes (sidebar + outlet)
          {
            element: <SuspenseWrap><AccountShell /></SuspenseWrap>,
            children: [
              { path: 'account', element: <SuspenseWrap><AccountOverviewPage /></SuspenseWrap> },
              { path: 'profile/edit', element: <SuspenseWrap><EditProfilePage /></SuspenseWrap> },
              { path: 'profile/edit/look', element: <SuspenseWrap><EditProfilePage /></SuspenseWrap> },
              { path: 'profile/edit/content', element: <SuspenseWrap><EditProfilePage /></SuspenseWrap> },
              { path: 'profile/edit/gst', element: <SuspenseWrap><EditProfilePage /></SuspenseWrap> },
              { path: 'profile/settings', element: <SuspenseWrap><AccountSettingsPage /></SuspenseWrap> },
              { path: 'profile/settings/notifications', element: <SuspenseWrap><AccountSettingsPage /></SuspenseWrap> },
              { path: 'profile/settings/privacy', element: <SuspenseWrap><AccountSettingsPage /></SuspenseWrap> },
              { path: 'profile/settings/seller', element: <SuspenseWrap><AccountSettingsPage /></SuspenseWrap> },
              { path: 'profile/billing', element: <SuspenseWrap><BillingPage /></SuspenseWrap> },
              { path: 'my-listings', element: <SuspenseWrap><MyListingsPage /></SuspenseWrap> },
              { path: 'saved', element: <Navigate to="/watchlist" replace /> },
              { path: 'watchlist', element: <SuspenseWrap><WatchlistPage /></SuspenseWrap> },
              { path: 'reviews', element: <SuspenseWrap><ReviewsPage /></SuspenseWrap> },
              { path: 'milestones', element: <SuspenseWrap><MilestonesPage /></SuspenseWrap> },
              { path: 'offers', element: <SuspenseWrap><OffersPage /></SuspenseWrap> },
              { path: 'orders', element: <SuspenseWrap><OrdersPage /></SuspenseWrap> },
              { path: 'orders/:id', element: <SuspenseWrap><OrderDetailPage /></SuspenseWrap> },
              { path: 'seller-orders', element: <SuspenseWrap><SellerOrdersPage /></SuspenseWrap> },
              { path: 'notifications', element: <SuspenseWrap><NotificationsPage /></SuspenseWrap> },
              { path: 'messages', element: <SuspenseWrap><MessagesLayout /></SuspenseWrap> },
              { path: 'messages/:conversationId', element: <SuspenseWrap><MessagesLayout /></SuspenseWrap> },
            ],
          },
        ],
      },

      // Admin-only routes (lazy-loaded, require admin access)
      // AdminShell wraps every /admin/* path; AdminPage reads activeTab from the URL.
      {
        element: <AdminRoute />,
        children: [
          {
            path: 'admin',
            element: <SuspenseWrap><AdminShell /></SuspenseWrap>,
            children: [
              { index: true, element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
              { path: 'users', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
              { path: 'listings', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
              { path: 'categories', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
              { path: 'bugs', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
              { path: 'conversations', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
              { path: 'ads', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
              { path: 'finance', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
              { path: 'features', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
              { path: 'pages', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
              { path: 'audit', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
            ],
          },
        ],
      },

      // Catch-all
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
